import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { inventoryService, InventoryLot } from '../../services/inventoryService';
import { vaccinationLotControls } from '../../auth/pageControls';
import { useRBAC } from '../../auth/useRBAC';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { useIsOffline } from '../../offline/OfflineNotice';
import { calendarDaysUntil, formatCalendarDate, formatInstantInZone } from '../../utils/dateUtils';
import { daysOpen, isPastInUseWindow, lotExpiry, suggestLot } from './fefo';
import { describeBalance, describeDraw } from './lotDraw';

/**
 * The lot a dose came from (spec §8-§12, ADR-0047). `auto` follows the earliest-expiring usable lot;
 * `lot` is one the vet picked; `manual` is a lot typed off the vial that inventory does not hold. An unknown
 * or short lot is recorded as such on the vaccination. An expired one (picked, or typed with a past expiry)
 * needs a tick, and only someone holding manage:expired_lot_doses may give it (ADR-0047 A5).
 */
export type LotSelection =
  | { mode: 'auto'; lotNumber?: string }
  | { mode: 'lot'; lotNumber: string; expiredConfirmed: boolean }
  | { mode: 'manual'; lotNumber: string; lotExpiresOn: string; manufacturer: string; expiredConfirmed?: boolean };

export const AUTO_LOT: LotSelection = { mode: 'auto' };

/** The lot fields a selection sends (the server finds the lot by number). */
export function lotPayload(selection: LotSelection): { lotNumber?: string; lotExpiresOn?: string; manufacturer?: string } {
  if (selection.mode === 'manual') {
    return {
      lotNumber: selection.lotNumber.trim() || undefined,
      lotExpiresOn: selection.lotExpiresOn || undefined,
      manufacturer: selection.manufacturer.trim() || undefined,
    };
  }
  return { lotNumber: selection.lotNumber || undefined };
}

interface VaccinationLotPickerProps {
  vaccineName: string;
  /** '' = Automatic: the server resolves the location the same way the save will. */
  locationId: string;
  doseMl: number | null;
  /** Doses of doseMl this lot gives (a barn run gives one per horse). */
  doseCount?: number;
  value: LotSelection;
  onChange: (next: LotSelection) => void;
  /** The day the dose was given (yyyy-MM-dd) when it is not today: an expired lot is judged against it, as the server does. */
  givenOn?: string;
  /** Reports an expired lot that has not been confirmed (or may not be), so the dialog can hold the save. */
  onNeedsConfirmation?: (needs: boolean) => void;
  disabled?: boolean;
}

const MANUAL = '__manual__';
const AUTO = '__auto__';

const VaccinationLotPicker: React.FC<VaccinationLotPickerProps> = ({
  vaccineName,
  locationId,
  doseMl,
  doseCount = 1,
  value,
  onChange,
  onNeedsConfirmation,
  givenOn,
  disabled,
}) => {
  const tz = useClinicTimezone();
  const isOffline = useIsOffline();
  const rbac = useRBAC();
  const can = vaccinationLotControls(rbac);
  // The name settles before the lots are read: each read runs the server's whole dispense resolution.
  const typed = vaccineName.trim();
  const [name, setName] = useState(typed);
  useEffect(() => {
    const timer = setTimeout(() => setName(typed), 400);
    return () => clearTimeout(timer);
  }, [typed]);
  const { data, isLoading, isError } = useQuery(
    ['vaccine-lots', name, locationId],
    () => inventoryService.getLotsForDispense(name, locationId || undefined),
    // A balance read from cache minutes later is how two vets draw the same last dose.
    { enabled: !!name && !isOffline, staleTime: 30 * 1000, retry: false },
  );
  const lots = useMemo(() => data?.lots ?? [], [data]);
  const suggested = useMemo(() => suggestLot(lots, tz), [lots, tz]);

  // Auto follows the suggestion as lots load or the location changes.
  useEffect(() => {
    if (value.mode === 'auto' && value.lotNumber !== suggested?.lotNumber) {
      onChange({ mode: 'auto', lotNumber: suggested?.lotNumber });
    }
  }, [value, suggested, onChange]);

  const chosen: InventoryLot | undefined =
    value.mode === 'manual' ? undefined : lots.find((l) => l.lotNumber === value.lotNumber);
  // Expired by the day the dose was given (the clinic's today unless backdated), the server's test (ADR-0047 A5). A typed
  // expiry is held to it like a picked lot: the server gates both.
  const givenDay = givenOn || formatInstantInZone(new Date(), tz, 'yyyy-MM-dd');
  const expiredBy = (expiresOn: string | null | undefined) => !!expiresOn && expiresOn.slice(0, 10) < givenDay;
  const expired =
    value.mode === 'lot'
      ? !!chosen && expiredBy(chosen.expiresOn)
      : value.mode === 'manual' && !!value.lotNumber.trim() && expiredBy(value.lotExpiresOn);
  const confirmable = value.mode === 'auto' ? null : value;
  const needsConfirmation = expired && !(can.recordExpiredLot && !!confirmable?.expiredConfirmed);
  useEffect(() => {
    onNeedsConfirmation?.(needsConfirmation);
  }, [needsConfirmation, onNeedsConfirmation]);

  if (!typed) return null;

  const selectValue = value.mode === 'manual' ? MANUAL : value.mode === 'auto' ? AUTO : value.lotNumber;

  const lotLabel = (lot: InventoryLot) => {
    const days = calendarDaysUntil(lot.expiresOn, tz);
    const open = daysOpen(lot, tz);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={600}>
          {lot.lotNumber}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          exp {formatCalendarDate(lot.expiresOn, 'MMM d, yyyy')} · {describeBalance(lot)}
          {lot.containerType === 'SINGLE_DOSE' ? ' single-dose' : ''}
        </Typography>
        {lotExpiry(lot, tz) === 'expired' && <Chip size="small" color="error" label="Expired" />}
        {lotExpiry(lot, tz) === 'expiring' && days !== null && <Chip size="small" color="warning" label={`${days}d left`} />}
        {open !== null && (
          <Chip
            size="small"
            variant="outlined"
            color={isPastInUseWindow(lot, tz) ? 'error' : 'default'}
            label={lot.inUseDays != null ? `open ${open}d of ${lot.inUseDays}d` : `open ${open}d`}
          />
        )}
        {suggested?.id === lot.id && <Chip size="small" color="success" label="Use first" />}
      </Box>
    );
  };

  return (
    <Box>
      <TextField
        select
        fullWidth
        size="small"
        label="Lot"
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === AUTO) onChange(AUTO_LOT);
          else if (v === MANUAL) onChange({ mode: 'manual', lotNumber: '', lotExpiresOn: '', manufacturer: '' });
          else onChange({ mode: 'lot', lotNumber: v, expiredConfirmed: false });
        }}
        helperText={
          isOffline
            ? 'Lots load when you are online. You can still type the lot from the vial.'
            : isError
              ? 'Could not load lots. You can still type the lot from the vial.'
              : !isLoading && lots.length === 0
                ? 'No lots recorded here. Type the lot from the vial, or save without one.'
                : undefined
        }
        SelectProps={{
          renderValue: (v) => {
            if (v === MANUAL) return 'Typed from the vial';
            const lot = v === AUTO ? suggested : lots.find((l) => l.lotNumber === v);
            if (v === AUTO && !lot) return 'No lot';
            return lot ? lotLabel(lot) : String(v);
          },
        }}
      >
        <MenuItem value={AUTO}>{suggested ? `Use first: ${suggested.lotNumber}` : 'No lot'}</MenuItem>
        {lots.map((lot) => (
          <MenuItem key={lot.id} value={lot.lotNumber}>
            {lotLabel(lot)}
          </MenuItem>
        ))}
        <MenuItem value={MANUAL}>Lot not listed: type it from the vial</MenuItem>
      </TextField>

      {chosen && doseMl !== null && doseMl > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {describeDraw(chosen, doseMl, doseCount)}
        </Typography>
      )}

      {value.mode === 'manual' && (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Lot number"
              size="small"
              fullWidth
              value={value.lotNumber}
              onChange={(e) => onChange({ ...value, lotNumber: e.target.value, expiredConfirmed: false })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Expiration date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={value.lotExpiresOn}
              onChange={(e) => onChange({ ...value, lotExpiresOn: e.target.value, expiredConfirmed: false })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Manufacturer"
              size="small"
              fullWidth
              value={value.manufacturer}
              onChange={(e) => onChange({ ...value, manufacturer: e.target.value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Saved on the record for traceability, and flagged so the lot can be added to inventory.
            </Typography>
          </Grid>
        </Grid>
      )}

      {expired && confirmable && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Lot {confirmable.mode === 'manual' ? confirmable.lotNumber.trim() : chosen?.lotNumber} expired on{' '}
          {formatCalendarDate(confirmable.mode === 'manual' ? confirmable.lotExpiresOn : chosen?.expiresOn, 'MMM d, yyyy')}.
          {can.recordExpiredLot ? (
            <FormControlLabel
              // Flex with no negative margin: a wrapped label stays inside the alert on a phone.
              sx={{ display: 'flex', ml: 0, mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={!!confirmable.expiredConfirmed}
                  onChange={(e) => onChange({ ...confirmable, expiredConfirmed: e.target.checked })}
                  disabled={disabled}
                />
              }
              label="This lot was given. Record it anyway."
            />
          ) : (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Ask a veterinarian or admin to record a dose from an expired lot.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default VaccinationLotPicker;
